import React, { useCallback, useEffect, useState } from 'react';
import { Container } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'lodash';
import LabelValueDisplay from '../../components/LabelValue/LabelValueDisplay';
import NetworkTable, { ColumnConfig, ColumnType } from '../../components/InterfacesTable/NetworkTable/NetworkTable';
import './HubTab.css';
import DeleteWANProfileModal from '../../components/Modals/DeleteProfileModal';
import AddPrivateHubModal from './AddPrivateHubModal';
import {
  getAllHubClientsData,
  selectHubClientData,
  getHubMtdUsageData,
  getHubServerStatusData,
  getActiveHubClientData,
  createPrivateHubServerData,
  getPrivateHubServersData,
  deletePrivateHubServerData,
  updatePrivateHubServerData,
} from '../../../../../actions/Users/authenticateOpenEdgeService';
import {
  SELECT_HUB_CLIENT,
  CREATE_PRIVATE_HUB_SERVER,
  DELETE_PRIVATE_HUB_SERVER,
  UPDATE_PRIVATE_HUB_SERVER,
} from '../../../../../actions/types';
import CustomLoader from '../../../../../components/Loader/CustomLoader';
import { formatBytes, getDataFromLocalStorage } from '../../../../../utils/util';


interface Props {
  selectedRow?: any;
  dateRegistered?: string;
}


const HubTab: React.FC<Props> = ({ selectedRow, dateRegistered = '' }) => {
  const dispatch = useDispatch();
  const getAllHubClients = useSelector((state: any) => state.authReducer?.getAllHubClients);
  const getAllHubClientsLoading = useSelector((state: any) => state.authReducer?.getAllHubClientsLoading);
  const selectHubClient = useSelector((state: any) => state.authReducer?.selectHubClient);
  const hubMtdUsage = useSelector((state: any) => state.authReducer?.hubMtdUsage);
  const hubMtdUsageLoading = useSelector((state: any) => state.authReducer?.hubMtdUsageLoading);
  const hubServerStatus = useSelector((state: any) => state.authReducer?.hubServerStatus);
  const activeHubClient = useSelector((state: any) => state.authReducer?.activeHubClient);
  const createPrivateHubServer = useSelector((state: any) => state.authReducer?.createPrivateHubServer);
  const createPrivateHubServerLoading = useSelector((state: any) => state.authReducer?.createPrivateHubServerLoading);
  const getPrivateHubServers = useSelector((state: any) => state.authReducer?.getPrivateHubServers);
  const deletePrivateHubServer = useSelector((state: any) => state.authReducer?.deletePrivateHubServer);
  const updatePrivateHubServer = useSelector((state: any) => state.authReducer?.updatePrivateHubServer);
  const updatePrivateHubServerLoading = useSelector((state: any) => state.authReducer?.updatePrivateHubServerLoading);

  const [sharedHubs, setSharedHubs] = useState<any[]>([]);
  const [privateHubs, setPrivateHubs] = useState<any[]>([]);
  const [selectedHub, setSelectedHub] = useState('');
  const [activeHubName, setActiveHubName] = useState('');
  const [pendingHub, setPendingHub] = useState<any>(null);
  const [hubModalMode, setHubModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingHub, setEditingHub] = useState<any>(null);
  const [pendingDeleteHub, setPendingDeleteHub] = useState<any>(null);

  const hubList = React.useMemo(() => [...sharedHubs, ...privateHubs], [sharedHubs, privateHubs]);

  const fetchAll = useCallback(() => {
    dispatch(getAllHubClientsData() as any);
    if (selectedRow?.k4Id && selectedRow?.deviceId) {
      dispatch(getHubMtdUsageData(selectedRow.k4Id, selectedRow.deviceId, dateRegistered || undefined) as any);
      dispatch(getHubServerStatusData(selectedRow.k4Id, selectedRow.deviceId) as any);
      dispatch(getActiveHubClientData(selectedRow.deviceId) as any);
      dispatch(getPrivateHubServersData(selectedRow.deviceId) as any);
    }
  }, [selectedRow?.k4Id, selectedRow?.deviceId, dateRegistered]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  useEffect(() => {
    if (_.isEmpty(getAllHubClients) || !getAllHubClients?.success) return;
    const list: any[] = getAllHubClients.result || [];
    setSharedHubs(list.map((h) => ({ ...h, isPrivate: false })));
  }, [getAllHubClients]);

  useEffect(() => {
    if (_.isEmpty(getPrivateHubServers) || !getPrivateHubServers?.status) return;
    const list: any[] = getPrivateHubServers.private_hub_servers || [];
    setPrivateHubs(list.map((h) => ({
      id: h.id,
      alias: h.name,
      domains: (h.hub_ips || '').split(',').map((d: string) => d.trim()).filter(Boolean),
      password: h.password ?? '',
      isPrivate: true,
    })));
  }, [getPrivateHubServers]);

  useEffect(() => {
    if (_.isEmpty(activeHubClient)) return;
    const name: string = activeHubClient?.hub_client?.name ?? '';
    if (name) {
      setActiveHubName(name);
      setSelectedHub(name);
    }
  }, [activeHubClient]);

  useEffect(() => {
    if (_.isEmpty(selectHubClient)) return;
    if (selectHubClient?.status) {
      toast.success(selectHubClient.msg || 'Hub client assigned successfully.', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      fetchAll();
    } else if (selectHubClient?.msg) {
      toast.error(selectHubClient.msg, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    dispatch({ type: SELECT_HUB_CLIENT, payload: {} });
  }, [selectHubClient]);

  useEffect(() => {
    if (_.isEmpty(createPrivateHubServer)) return;
    if (createPrivateHubServer?.status) {
      toast.success(createPrivateHubServer.msg || 'Private hub created successfully.', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      setHubModalMode(null);
      setEditingHub(null);
      if (selectedRow?.deviceId) dispatch(getPrivateHubServersData(selectedRow.deviceId) as any);
    } else if (createPrivateHubServer?.msg) {
      toast.error(createPrivateHubServer.msg, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    dispatch({ type: CREATE_PRIVATE_HUB_SERVER, payload: {} });
  }, [createPrivateHubServer]);

  useEffect(() => {
    if (_.isEmpty(updatePrivateHubServer)) return;
    if (updatePrivateHubServer?.status) {
      toast.success(updatePrivateHubServer.msg || 'Private hub updated successfully.', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      setHubModalMode(null);
      setEditingHub(null);
      if (selectedRow?.deviceId) dispatch(getPrivateHubServersData(selectedRow.deviceId) as any);
    } else if (updatePrivateHubServer?.msg) {
      toast.error(updatePrivateHubServer.msg, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    dispatch({ type: UPDATE_PRIVATE_HUB_SERVER, payload: {} });
  }, [updatePrivateHubServer]);

  useEffect(() => {
    if (_.isEmpty(deletePrivateHubServer)) return;
    if (deletePrivateHubServer?.status) {
      toast.success(deletePrivateHubServer.msg || 'Private hub removed successfully.', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      if (selectedRow?.deviceId) dispatch(getPrivateHubServersData(selectedRow.deviceId) as any);
    } else if (deletePrivateHubServer?.msg) {
      toast.error(deletePrivateHubServer.msg, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    dispatch({ type: DELETE_PRIVATE_HUB_SERVER, payload: {} });
  }, [deletePrivateHubServer]);

  const handleHubSelect = (value: string) => {
    const hub = hubList.find((h) => h.alias === value);
    if (!hub) return;
    setPendingHub(hub);
  };

  const handleConfirm = () => {
    if (!pendingHub) return;
    setSelectedHub(pendingHub.alias);
    const userName = getDataFromLocalStorage('loggedInEmail') || '';
    const hub_ips = Array.isArray(pendingHub.domains)
      ? pendingHub.domains.join(',')
      : (pendingHub.domains || '');
    dispatch(selectHubClientData(
      selectedRow?.deviceId,
      pendingHub.alias,
      hub_ips,
      pendingHub.password || '',
      userName,
      selectedRow?.k4Id,
      pendingHub.isPrivate ? 'private' : 'public',
    ) as any);
    setPendingHub(null);
  };

  const handleSaveHub = (name: string, hubIps: string, password: string) => {
    if (!selectedRow?.deviceId) return;
    if (hubModalMode === 'edit' && editingHub) {
      dispatch(updatePrivateHubServerData(editingHub.id, selectedRow.deviceId, name, hubIps, password) as any);
    } else {
      dispatch(createPrivateHubServerData(selectedRow.deviceId, name, hubIps, password) as any);
    }
  };

  const handleDeleteHub = () => {
    if (!pendingDeleteHub || !selectedRow?.deviceId) return;
    dispatch(deletePrivateHubServerData(pendingDeleteHub.id, selectedRow.deviceId) as any);
    setPendingDeleteHub(null);
  };

  const handleTableAction = (action: string, _key: string, row: any) => {
    if (action === 'delete') setPendingDeleteHub(row);
    if (action === 'setting') {
      setEditingHub(row);
      setHubModalMode('edit');
    }
  };

  const closeHubModal = () => {
    setHubModalMode(null);
    setEditingHub(null);
  };

  const hubNames = hubList.map((h) => h.alias);
  const mtdBytes: number = hubMtdUsage?.data?.[0]?.usage ?? null;
  const mtdTotalBytes: number = hubMtdUsage?.data?.[0]?.total_quota ?? null;
  const hubEnabled: boolean = activeHubClient?.hub_client?.enabled === true;
  const tunnelOnline: boolean = hubServerStatus?.oe_response?.hub_status === true;
  const hasTotalQuota: boolean = mtdTotalBytes !== null && mtdTotalBytes > 0;

  const columns: ColumnConfig[] = [
    {
      label: 'Hub Name',
      key: 'alias',
      type: ColumnType.text,
      width: '280px',
      maxWidth: '280px',
      renderCell: (value) => (
        <span style={{ fontWeight: value === activeHubName ? 700 : 'normal' }}>{value}</span>
      ),
    },
    {
      label: 'Domain / IP:Port',
      key: 'domains',
      type: ColumnType.text,
      width: '400px',
      maxWidth: '400px',
      renderCell: (value) => {
        const entries: string[] = Array.isArray(value) ? value : [value];
        return (
          <span>
            {entries.map((entry, i) => (
              <span key={i}>
                {i > 0 && ', '}
                {entry}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      label: 'Type',
      key: 'isPrivate' as any,
      type: ColumnType.text,
      width: '140px',
      maxWidth: '140px',
      renderCell: (value) => (value ? 'Private' : 'Public'),
    },
    {
      label: 'Actions',
      key: 'custom',
      type: ColumnType.actions,
      width: '110px',
      maxWidth: '110px',
      actions: (row: any) => {
        if (!row?.isPrivate) return [];
        return row.alias === activeHubName ? ['setting'] : ['setting', 'delete'];
      },
    },
  ];

  return (
    <div className="internalPadding">
      <Container maxWidth="xl" className="app-container">
        {getAllHubClientsLoading && hubList.length === 0 ? (
          <CustomLoader showLoader={true} loadingText="Please wait..." />
        ) : (
          <>
            <div className="hub-tab-controls">
              <div className="hub-tab-dropdown">
                <LabelValueDisplay
                  label="Hub Dropoff"
                  isDropdown
                  value={selectedHub}
                  dropdownOptions={hubNames}
                  onChange={(e: any) => handleHubSelect(e?.target?.value ?? e)}
                  placeholder={hubNames.length === 0 ? 'No Hubs available' : 'Select a Hub'}
                />
              </div>
              <div className="hub-tab-right-group">
                {activeHubClient?.hub_client && (
                  <>
                    <div className="hub-tab-server-status">
                      <span className="hub-tab-server-status-label">Service</span>
                      <span className="hub-service-text">
                        {hubEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    {hubEnabled && (
                      <div className="hub-tab-server-status">
                        <span className="hub-tab-server-status-label">Tunnel</span>
                        <div className="hub-status-indicator">
                          <span className={`hub-status-dot ${tunnelOnline ? 'hub-status-dot--active' : 'hub-status-dot--inactive'}`} />
                          <span className="hub-status-text">
                            {tunnelOnline ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="hub-tab-mtd">
                  <span className="hub-tab-mtd-label">MTD Usage</span>
                  <span className="hub-tab-mtd-value">
                    {hubMtdUsageLoading && mtdBytes === null
                      ? '...'
                      : mtdBytes !== null
                        ? (
                          <>
                            <span style={hasTotalQuota && mtdBytes > mtdTotalBytes ? { color: 'red' } : {}}>
                              {formatBytes(mtdBytes, 2, 'MB')}
                            </span>
                            {hasTotalQuota && (
                              <>
                                {' / '}
                                {formatBytes(mtdTotalBytes, 0)}
                              </>
                            )}
                          </>
                        )
                        : '—'
                    }
                  </span>
                </div>
              </div>
            </div>
            <div className="hub-tab-table">
              <NetworkTable
                data={hubList}
                columns={columns}
                onActionClick={handleTableAction}
                showFilterBar
                addButtonLabel="Add Hub"
                onAddClick={() => { setEditingHub(null); setHubModalMode('add'); }}
              />
            </div>
          </>
        )}
      </Container>

      <DeleteWANProfileModal
        open={!!pendingHub}
        onClose={() => setPendingHub(null)}
        onConfirm={handleConfirm}
        title="Alert"
        titleWithDeleteIcon={true}
        confirmText="Confirm"
        cancelText="Cancel"
        entityLabel={`Hub Dropoff Configuration will be changed to ${pendingHub?.alias}.`}
        entityName={undefined}
        warningMessage="Existing traffic sessions will be disrupted with this change."
      />

      <AddPrivateHubModal
        open={!!hubModalMode}
        mode={hubModalMode ?? 'add'}
        initialName={editingHub?.alias ?? ''}
        initialHubIps={Array.isArray(editingHub?.domains) ? editingHub.domains.join(', ') : (editingHub?.domains ?? '')}
        initialPassword={editingHub?.password ?? ''}
        onClose={closeHubModal}
        onSave={handleSaveHub}
        saving={createPrivateHubServerLoading || updatePrivateHubServerLoading}
      />

      <DeleteWANProfileModal
        open={!!pendingDeleteHub}
        onClose={() => setPendingDeleteHub(null)}
        onConfirm={handleDeleteHub}
        title="Delete Private Hub"
        titleWithDeleteIcon={true}
        confirmText="Delete"
        cancelText="Cancel"
        entityLabel={`Private hub "${pendingDeleteHub?.alias}" will be removed.`}
        entityName={undefined}
        warningMessage="This action cannot be undone."
      />
    </div>
  );
};

export default HubTab;
